'use client'

import React, { useEffect, useState, useCallback } from "react";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { useGetRQLDetailsQuery, useCreateRQLMutation, useGetAllDefectKeysQuery } from '@/redux/slices/preship/preship-api-slice';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { Link } from "react-router-dom";
import { Textarea } from "@/components/ui/textarea";
import AlertTooltip from '@/components/alert-tooltip';
import { cn } from "@/lib/utils";
import { showSuccessToast, showErrorToast } from "../toast/toast";
import { useGetLpnNumbersQuery } from "@/redux/slices/lpn/lpnApiSlice";
import { DefectKey, RQLData, RqlService, RQLDefect } from "@/services/RqlService";
import { LpnService } from "@/services/LpnService";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { formRQLSchema } from "@/lib/schemas/forms/rql-schema";
import { RQLService } from "@/services/RQLService";
import { Skeleton } from "@/components/ui/skeleton";
import EditBanner from "./EditBanner";
import { Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useStepperContext } from "@/context/stepper-provider";
interface RqlData {
  id?: number;
  palletId: number;
  variant: string;
  targetSampleSize: string;
  actualSampleSize: string;
  dateOfInspection: string;
  packageType: string;
  inspectedBy: string;
  totalDefects: number;
  percentDefects: number;
  passFail: string;
  unclassifiedDefects: string;
  rqlNotes: string;
  defects?: RQLDefect[];
}

interface RqlDefect {
  defectKeyId: number;
  defectType: 'critical' | 'major1' | 'major2' | 'minor';
  defectPercentage: string;
  packageType: string;
}

interface FormValues {
  rqlData: RqlData;
  rqlDefects: RqlDefect[];
}

const validationSchema = Yup.object().shape({
  rqlData: Yup.object().shape({
    targetSampleSize: Yup.number().required('Target Sample Size is required'),
    actualSampleSize: Yup.number().required('Actual Sample Size is required'),
    dateOfInspection: Yup.date().required('Date of Inspection is required'),
    packageType: Yup.string().required('Package Type is required'),
    inspectedBy: Yup.string().required('Inspected By is required'),
    totalDefects: Yup.number(),
    percentDefects: Yup.number(),
    passFail: Yup.string().oneOf(['Pass', 'Fail'], 'Pass/Fail must be either Pass or Fail'),
    unclassifiedDefects: Yup.string(),
    rqlNotes: Yup.string(),
  }),
  rqlDefects: Yup.array().of(
    Yup.object().shape({
      defectKeyId: Yup.number().required('Defect Key ID is required'),
      defectType: Yup.string().oneOf(['critical', 'major1', 'major2', 'minor'], 'Invalid defect type'),
      defectPercentage: Yup.string(),
    })
  ),
});

interface RqlProps {
  variant: 'preShip' | 'postShip' | 'postStack';
  palletId?: number;
  tabName: string;
  stage: string;
  isEditing?: boolean;
  onFormChange?: (hasChanges: boolean) => void;
  isOffline?: boolean;
  onOfflineSubmit?: (values: any) => Promise<void>;
  setIsLoading?: (isLoading: boolean) => void;
}

const defPer: { [key: string]: number } = {
  'critical': 0,
  'major1': 0.5,
  "major2": 2.9,
  "minor": 7.5
};

export default function Rql({
  variant,
  palletId,
  tabName,
  stage,
  onFormChange,
  isOffline,
  onOfflineSubmit,
  setIsLoading
}: RqlProps) {
  const { activePallet } = useSelector((state: RootState) => state.pallets);
  const [initialValues, setInitialValues] = useState<FormValues | null>(null);
  const { data: rqlData, isLoading: isRqlLoading, error: rqlError, refetch } = useGetRQLDetailsQuery(
    { palletId: activePallet?.id || 0, variant },
    { skip: !activePallet, refetchOnMountOrArgChange: true, refetchOnFocus: true }
  );
  const {projectId}=useSelector((state:RootState)=>state.define)
  const [isEditing, setIsEditing] = useState(false);
  const [offlineRqlData, setOfflineRqlData] = useState<RQLData | null>(null)
  const [offlineDefectKeys, setOfflineDefectKeys] = useState<DefectKey[]>([])
  const [offlineLpnData, setOfflineLpnData] = useState<any>(null)
  const { data: defectKeysData, isLoading: isDefectKeysLoading, error: defectKeysError } = useGetAllDefectKeysQuery('');
  const [createOrUpdateRQL] = useCreateRQLMutation();
  const { data: lpnData, isLoading: lpnLoading, isFetching: lpnFetching } = useGetLpnNumbersQuery(activePallet?.id);
  console.log(offlineDefectKeys, 'offlineDefectKeys', typeof (offlineDefectKeys), offlineDefectKeys.length, offlineRqlData)
  console.log(defectKeysData, 'defectKeysData', rqlError, 'rqlError')
  const navigate=useNavigate()
  const { stepper, steps } = useStepperContext();
  const { current, goTo } = stepper;
  const formik = useFormik<FormValues>({
    initialValues: {
      rqlData: {
        palletId: Number(activePallet?.id) || 0,
        variant: variant,
        targetSampleSize: '',
        actualSampleSize: '',
        dateOfInspection: '',
        packageType: "bottle",
        inspectedBy: '',
        totalDefects: 0,
        percentDefects: 0,
        passFail: '',
        unclassifiedDefects: '',
        rqlNotes: '',
      },
      rqlDefects: [],
    },
    // validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {

      try {
        if (isOffline) {
          const filteredRqlDefects = values.rqlDefects
            .filter(defect => defect.defectPercentage !== '')
            .map(defect => ({
              defectKeyId: defect.defectKeyId,
              defectType: defect.defectType,
              defectPercentage: defect.defectPercentage,
            }));

          const payload = {
            rqlData: {
              ...values.rqlData,
              ...(values.rqlData.dateOfInspection && !isNaN(Date.parse(values.rqlData.dateOfInspection)) ? {} : { dateOfInspection: undefined }),
              palletId: Number(activePallet?.id) || 0,
              variant: variant,
            },
            rqlDefects: filteredRqlDefects,
          };
          console.log(payload, 'payload')
          await RqlService.createRQL(payload);
          showSuccessToast(`${variant} RQL data saved successfully`)
          return;
        } else {
          const filteredRqlDefects = values.rqlDefects
            .filter(defect => defect.defectPercentage !== '')
            .map(defect => ({
              defectKeyId: defect.defectKeyId,
              defectType: defect.defectType,
              defectPercentage: defect.defectPercentage,
            }));

          const payload = {
            rqlData: {
              ...values.rqlData,
              ...(values.rqlData.dateOfInspection && !isNaN(Date.parse(values.rqlData.dateOfInspection)) ? {} : { dateOfInspection: undefined }),
              palletId: Number(activePallet?.id) || 0,
              variant: variant,
            },
            rqlDefects: filteredRqlDefects,
          };
          console.log(payload, 'payload')
          await createOrUpdateRQL(payload).unwrap();
          showSuccessToast(`${variant} RQL data saved successfully`);
        }

      } catch (error) {
        console.error(error, 'rql save error');
        showErrorToast(`Failed to save ${variant} RQL data`);
      }
    }
  });
  useEffect(() => {
    setIsLoading(formik.isSubmitting)
  }, [formik.isSubmitting, setIsLoading]);
  useEffect(() => {
    if (isOffline) {
      const getData = async () => {
        try {
          const res = await LpnService.getLpnNumbers(Number(activePallet?.id))
          setOfflineLpnData(res.lpnNumbers)
        } catch (error) {
          console.error('Error fetching LPN numbers:', error);
        }
      }
      getData()
    }
  }, [isOffline, activePallet])
  useEffect(() => {
    if (isOffline) {
      const getData = async () => {
        try {
          const res = await RqlService.getAllDefectKeys()
          setOfflineDefectKeys(res.defectKeys)
        } catch (error) {
          console.error('Error fetching RQL data:', error);
        }
      }
      getData()
    }
  }, [isOffline])
  useEffect(() => {
    if (isOffline) {
      const getData = async () => {
        try {
          const res = await RqlService.getRQLDetails(Number(activePallet?.id), variant)
          setOfflineRqlData(res.rqls[0])
        } catch (error) {
          console.error('Error fetching RQL data:', error);
        }
      }
      getData()
    }
  }, [isOffline, activePallet])
  useEffect(() => {
    if (defectKeysData?.defectKeys && !isOffline) {
      const initialRqlDefects = defectKeysData.defectKeys.map((defectKey: any) => ({
        defectKeyId: defectKey.id,
        defectType: '',
        defectPercentage: '',
        packageType: defectKey.packageType
      }));

      if (rqlData && rqlData.rqls && rqlData.rqls.length > 0) {
        const rql = rqlData.rqls[0];
        const newValues = {
          rqlData: {
            id: rql.id,
            packageType: rql?.packageType || 'bottle',
            palletId: rql.palletId,
            variant: rql.variant,
            targetSampleSize: rql.targetSampleSize,
            actualSampleSize: rql.actualSampleSize,
            dateOfInspection: rql.dateOfInspection,
            inspectedBy: rql.inspectedBy,
            totalDefects: rql.totalDefects,
            percentDefects: rql.percentDefects,
            passFail: rql.passFail,
            unclassifiedDefects: rql.unclassifiedDefects,
            rqlNotes: rql.rqlNotes,
          },
          rqlDefects: initialRqlDefects.map((defect: any) => {
            const existingDefect = rql.defects.find((d: any) => d.defectKeyId === defect.defectKeyId);
            return existingDefect ? { ...defect, ...existingDefect } : defect;
          }),
        };
        formik.setValues(newValues);
        setInitialValues(newValues);
      } else {
        const newValues = {
          rqlData: {
            palletId: Number(activePallet?.id) || 0,
            packageType: 'bottle',
            variant: variant,
            targetSampleSize: '',
            actualSampleSize: '',
            dateOfInspection: '',
            inspectedBy: '',
            totalDefects: 0,
            percentDefects: 0,
            passFail: '',
            unclassifiedDefects: '',
            rqlNotes: '',
          },
          rqlDefects: initialRqlDefects,
        };
        formik.setValues(newValues);
        setInitialValues(newValues);
      }
    }
  }, [rqlData, defectKeysData, activePallet]);
  useEffect(() => {
    if (isOffline && offlineDefectKeys && offlineDefectKeys.length > 0) {
      const initialRqlDefects = offlineDefectKeys.map((defectKey: any) => ({
        defectKeyId: defectKey.id,
        // defectType: '',
        // defectPercentage: '',
        packageType: defectKey.packageType,
        name: defectKey.name,
        ...defectKey
      }));
      console.log(initialRqlDefects, 'initialRqlDefects')
      if (offlineRqlData) {
        const rql = offlineRqlData
        const newValues = {
          rqlData: {
            id: rql.id,
            packageType: rql?.packageType || 'bottle',
            palletId: rql.palletId,
            variant: rql.variant,
            targetSampleSize: rql.targetSampleSize,
            actualSampleSize: rql.actualSampleSize,
            dateOfInspection: rql.dateOfInspection,
            inspectedBy: rql.inspectedBy,
            totalDefects: rql.totalDefects,
            percentDefects: rql.percentDefects,
            passFail: rql.passFail,
            unclassifiedDefects: rql.unclassifiedDefects,
            rqlNotes: rql.rqlNotes,
          },
          rqlDefects: initialRqlDefects.map((defect: any) => {
            const existingDefect = rql?.defects?.find((d: any) => d.defectKeyId === defect.defectKeyId);
            return existingDefect ? { ...defect, ...existingDefect } : defect;
          }),
        };
        formik.setValues(newValues);
        setInitialValues(newValues);
      } else {
        const newValues = {
          rqlData: {
            palletId: Number(activePallet?.id) || 0,
            packageType: 'bottle',
            variant: variant,
            targetSampleSize: '',
            actualSampleSize: '',
            dateOfInspection: '',
            inspectedBy: '',
            totalDefects: 0,
            percentDefects: 0,
            passFail: '',
            unclassifiedDefects: '',
            rqlNotes: '',
          },
          rqlDefects: initialRqlDefects,
        };
        formik.setValues(newValues);
        setInitialValues(newValues);
      }
    }
  }, [isOffline, offlineRqlData, activePallet, offlineDefectKeys]);

  useEffect(() => {
    if (onFormChange && initialValues) {
      const hasChanges = JSON.stringify(formik.values) !== JSON.stringify(initialValues);
      onFormChange(hasChanges);
    }
  }, [formik.values, onFormChange, initialValues]);

  useEffect(() => {
    refetch();
  }, [activePallet?.id, refetch]);
  const handleUploadImages=()=>{

    navigate(`/projects/edit-project/${projectId}?imageTab=${variant==='preShip'?'Pre-Ship Rql':variant==='postShip'?'Post-Ship Rql':variant==='postStack'?'Post-Stack Rql':''}&palletId=${activePallet?.id}`)
    goTo(10)
  }
  const handleDefectInputChange = (defectKeyId: number, defectType: string, value: string) => {
    const updatedRqlDefects = formik.values.rqlDefects.map((d) => {
      if (defectKeyId === d.defectKeyId) {
        return {
          ...d,
          defectType: defectType,
          defectPercentage: value,
        };
      } else {
        return d;
      }
    });
    formik.setFieldValue('rqlDefects', updatedRqlDefects);
  };

  const packageTypeOptions = [
    { label: "Bottle", value: "bottle" },
    { label: "Can", value: "can" }
  ];
  useEffect(() => {
    return () => {
      if (isEditing) {
        setIsEditing(false)
      }
    }
  }, [isEditing])

  // Update keyboard navigation handler to better support moving through disabled cells
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>, defectIndex: number, columnIndex: number) => {
    // Get filterable defects that are currently displayed in the table
    const visibleDefects = formik.values.rqlDefects.filter((d) => d.packageType === formik.values.rqlData.packageType);

    let targetRow = defectIndex;
    let targetCol = columnIndex;

    switch (e.key) {
      case 'ArrowUp':
        targetRow = Math.max(0, defectIndex - 1);
        break;
      case 'ArrowDown':
      case 'Enter': // Adding Enter key to behave like Down arrow - Excel behavior
        targetRow = Math.min(visibleDefects.length - 1, defectIndex + 1);
        break;
      case 'ArrowLeft':
        targetCol = Math.max(0, columnIndex - 1);
        break;
      case 'ArrowRight':
        targetCol = Math.min(3, columnIndex + 1);
        break;
      default:
        return;
    }

    // Prevent default behavior, including form submission for Enter key
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(e.key)) {
      e.preventDefault();

      // Find and focus the next element
      const nextEl = document.querySelector(`[data-row="${targetRow}"][data-col="${targetCol}"]`) as HTMLElement | null;
      nextEl?.focus();
    }
  }, [formik.values.rqlDefects, formik.values.rqlData.packageType]);

  if (isDefectKeysLoading || lpnFetching) return (
    <div className="space-y-6 w-full animate-pulse">
      {/* Header skeleton */}
      <div className="flex justify-between items-center">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-10 w-32" />
      </div>

      {/* Form fields skeleton */}
      <div className="grid grid-cols-2 gap-8">
        <div className="space-y-4">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-10 w-full" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-10 w-full" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-10 w-full" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>

      {/* Tabs skeleton */}
      <div>
        <div className="flex gap-3 border-b pb-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-32 rounded-md" />
          ))}
        </div>

        {/* Table skeleton */}
        <div className="mt-4 border rounded-md overflow-hidden">
          <div className="border-b bg-gray-50">
            <div className="grid grid-cols-5 p-3">
              {Array(5).fill(0).map((_, i) => (
                <Skeleton key={i} className="h-6 w-full mx-2" />
              ))}
            </div>
          </div>
          <div className="p-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="grid grid-cols-5 gap-2 p-2 border-b last:border-0">
                {Array(5).fill(0).map((_, j) => (
                  <Skeleton key={j} className="h-10 w-full" />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
  // if (defectKeysError) return <div>Error loading defect keys</div>;

  const getDefectSummary = () => {
    const summary: { [key: string]: { total: number, percentage: number, passFail: string } } = {
      critical: { total: 0, percentage: 0, passFail: 'Pass' },
      major1: { total: 0, percentage: 0, passFail: 'Pass' },
      major2: { total: 0, percentage: 0, passFail: 'Pass' },
      minor: { total: 0, percentage: 0, passFail: 'Pass' },
    };

    formik.values.rqlDefects.forEach(defect => {
      if (defect.packageType === formik.values.rqlData.packageType && defect.defectPercentage) {
        const defectCount = Number(defect.defectPercentage);
        summary[defect.defectType].total += defectCount;
      }
    });

    Object.keys(summary).forEach(defectType => {
      const percentage = (summary[defectType].total / Number(formik.values.rqlData.actualSampleSize)) * 100;
      summary[defectType].percentage = percentage;
      summary[defectType].passFail = percentage > defPer[defectType] ? 'Fail' : 'Pass';
    });

    return summary;
  };

  const defectSummary = getDefectSummary();
  console.log(formik.values, 'formik.values', formik.values.rqlDefects, formik.values.rqlDefects[0])
  return (
    <div className=" space-y-4">
      {!isOffline && <EditBanner

        stage={stage}
        palletId={palletId}
        component={tabName}
        onEditStateChange={setIsEditing}
      />}
      <form
        id={`rql-table-${activePallet?.id}`}
        className={`${isEditing ? "" : "pointer-events-none"} space-y-4`}
        onSubmit={formik.handleSubmit}
        onKeyDown={(e) => {
          // Prevent form submission when Enter is pressed inside the table
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
      >
        {lpnData?.lpnNumbers && (
          <div>
            Pallets with RQL state: {lpnData.lpnNumbers.filter((lpn: any) => lpn?.rqlState === 'yes').map((lpn: any) => lpn.lpnNo).join(', ')}
          </div>
        )}

        <div className="flex items-center gap-2 my-2">
          RQL&apos;s done for:
          {Array.from(new Set(formik.values.rqlDefects.filter(d => d.defectPercentage && !isNaN(Number(d.defectPercentage))).map(d => d.packageType))).map((packageType, i) => (
            <div
              key={i}
              onClick={() => formik.setFieldValue('rqlData.packageType', packageType)}
              className={cn(
                "text-sm py-1 px-2 cursor-pointer",
                packageType === formik.values.rqlData.packageType ? "bg-blue rounded-md text-white" : ""
              )}
            >
              {packageType === 'bottle' ? 'Bottle' : packageType === 'can' ? 'Can' : packageType === 'label' ? 'Label' : packageType === 'plasticClosure' ? 'Plastic Closure' : packageType === 'corrugates_assembly' ? 'Corrugates Assembly' : ''}
            </div>
          ))}
        </div>

        <div className=" flex gap-6 items-center">
          <div className=" w-2/5">
          <div className=" flex items-center border border-gray-300">
          <div className="bg-gray-100 border-r border-gray-300 p-2">
            <label className="text-sm font-medium">RQL Type</label>
          </div>
          <Select
            value={formik.values.rqlData.packageType}
            onValueChange={(value) => {
              if (value !== '') {
                formik.setFieldValue('rqlData.packageType', value)
              }
            }}
          >
            <SelectTrigger className="flex-1 rounded-none bg-transparent border-none">
              <SelectValue placeholder="Select RQL Type" />
            </SelectTrigger>
            <SelectContent>
              {
                isOffline ?
                  offlineDefectKeys?.length > 0 && [...new Set(offlineDefectKeys?.map((key: any) => key.packageType))].map((type: any) => (
                    <SelectItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </SelectItem>
                  )) :
                  defectKeysData?.defectKeys?.length > 0 && [...new Set(defectKeysData?.defectKeys?.map((key: any) => key.packageType))].map((type: any) => (
                    <SelectItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </SelectItem>
                  ))}
            </SelectContent>
          </Select>
        </div>
        {formik.touched.rqlData?.packageType && formik.errors.rqlData?.packageType && (
          <AlertTooltip message={formik.errors.rqlData.packageType} />
        )}

          </div>

          <div>
          <Button
          type='button'
          onClick={handleUploadImages}
          className="bg-white w-fit flex-1 flex-center h-[40px] justify-center text-[#64748B] relative border-dashed border-2 hover:bg-white"
        >
         
              <Upload className="mr-2" size={20} />
              <span>Upload Images</span>
        </Button>
          </div>
          
        </div>
        <div className="flex border border-gray-300">
          <div className="w-2/5 flex items-center">
            <div className="bg-gray-100 border-r border-gray-300 p-2">
              <label className="text-sm font-medium">Actual Sample Size</label>
            </div>
            <Input
              type='number'
              placeholder="Enter sample size"
              {...formik.getFieldProps('rqlData.actualSampleSize')}
              className="flex-1 rounded-none border-none"
            />
          </div>
          <div className="w-3/5 flex items-center border-l border-gray-300">
            <div className="bg-gray-100 border-r border-gray-300 p-2 w-1/2">
              <label className="text-sm font-medium">Date Of Inspection</label>
            </div>
            <DatePicker
              value={formik.values.rqlData.dateOfInspection ? new Date(formik.values.rqlData.dateOfInspection) : null}
              onChange={(date: Date | null) => formik.setFieldValue('rqlData.dateOfInspection', date?.toISOString())}
            />
          </div>
        </div>
        {formik.touched.rqlData?.actualSampleSize && formik.errors.rqlData?.actualSampleSize && (
          <AlertTooltip message={formik.errors.rqlData.actualSampleSize} />
        )}
        {formik.touched.rqlData?.dateOfInspection && formik.errors.rqlData?.dateOfInspection && (
          <AlertTooltip message={formik.errors.rqlData.dateOfInspection} />
        )}

        <div className="flex border border-gray-300">
          <div className="w-2/5 flex items-center">
            <div className="bg-gray-100 border-r border-gray-300 p-2">
              <label className="text-sm font-medium">Target Sample Size by QA </label>
            </div>
            <Input
              type='number'
              placeholder="Enter target sample size"
              {...formik.getFieldProps('rqlData.targetSampleSize')}
              className="flex-1 rounded-none border-none"
            />
          </div>
          <div className="w-3/5 flex items-center border-l border-gray-300">
            <div className="bg-gray-100 border-r border-gray-300 p-2 w-1/2">
              <label className="text-sm font-medium">Inspected By</label>
            </div>
            <Input
              placeholder="Enter inspector name"
              {...formik.getFieldProps('rqlData.inspectedBy')}
              className="flex-1 rounded-none border-none"
            />
          </div>
        </div>
        {formik.touched.rqlData?.targetSampleSize && formik.errors.rqlData?.targetSampleSize && (
          <AlertTooltip message={formik.errors.rqlData.targetSampleSize} />
        )}
        {formik.touched.rqlData?.inspectedBy && formik.errors.rqlData?.inspectedBy && (
          <AlertTooltip message={formik.errors.rqlData.inspectedBy} />
        )}

        <Table className="border-collapse">
          <TableHeader>
            <TableRow>
              <TableHead className="border border-gray-300 text-black p-2 text-sm font-bold bg-gray-100 text-center" colSpan={5}>
                Pallet Comparison
              </TableHead>
            </TableRow>
            <TableRow>
              <TableHead className="border border-gray-300 text-black p-2 text-sm font-bold bg-gray-100 text-left w-2/5">
                Finished Bottle Defects
              </TableHead>
              {["Critical", "Major 1", "Major 2", "Minor"].map((head) => (
                <TableHead key={head} className="border border-gray-300 text-black p-2 text-sm font-bold bg-gray-100 text-center">
                  {head}
                </TableHead>
              ))}
            </TableRow>
            <TableRow>
              <TableHead className="border border-gray-300 text-black p-2 text-sm font-bold bg-gray-100 text-left w-2/5">
                RQL Level
              </TableHead>
              {["0%", "0.5%", "2.9%", "7.5%"].map((head) => (
                <TableHead key={head} className="border border-gray-300 text-red-500 p-2 text-sm font-bold bg-gray-100 text-center">
                  {head}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {formik.values.rqlDefects.filter((d) => d.packageType === formik.values.rqlData.packageType).map((defect, defectIndex) => {
              const defectKey = isOffline ? offlineDefectKeys?.find((dk: any) => dk.id === defect.defectKeyId) : defectKeysData?.defectKeys?.find((dk: any) => dk.id === defect.defectKeyId);
              const defectType = defectKey?.defectType?.toLowerCase();

              return (
                <TableRow key={defect.defectKeyId}>
                  <TableCell className="border border-gray-300 bg-gray-50 p-2 text-sm font-medium">
                    {defectKey?.name}
                  </TableCell>
                  {['critical', 'major1', 'major2', 'minor'].map((fieldKey, colIndex) => {
                    // Determine if this cell should be editable
                    const isEditable = defectType === fieldKey;

                    return (
                      <TableCell key={fieldKey} className={cn("border border-gray-300 p-0", !isEditable && "bg-gray-200")}>
                        <Input
                          data-row={defectIndex}
                          data-col={colIndex}
                          className="w-full h-9 border-none bg-transparent"
                          placeholder={!isEditable ? '' : "Enter"}
                          type="number"
                          value={isEditable ? defect.defectPercentage : ''}
                          onChange={(e) => handleDefectInputChange(defectKey.id, fieldKey, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, defectIndex, colIndex)}
                          // Making all cells tabbable and removing disabled attribute
                          tabIndex={0}
                          // Instead of disabled, use readOnly for non-editable cells
                          readOnly={!isEditable}
                          // CSS styling to make it look disabled but still focusable
                          style={!isEditable ? { pointerEvents: 'none', opacity: '0.7' } : {}}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Table className="border-collapse">
          <TableBody>
            <TableRow>
              <TableCell className="border border-gray-300 bg-gray-50 p-2 text-sm font-medium w-2/5">Total Defects</TableCell>
              {['critical', 'major1', 'major2', 'minor'].map((defectType) => (
                <TableCell key={defectType} className="border border-gray-300 p-0">
                  <Input
                    value={defectSummary[defectType].total}
                    readOnly
                    className="w-full h-9 border-none bg-transparent text-center"
                  />
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell className="border border-gray-300 bg-gray-50 p-2 text-sm font-medium w-2/5">Percentage Defects</TableCell>
              {['critical', 'major1', 'major2', 'minor'].map((defectType) => (
                <TableCell key={defectType} className="border border-gray-300 p-0">
                  <Input
                    value={`${defectSummary[defectType].percentage.toFixed(2)}%`}
                    readOnly
                    className="w-full h-9 border-none bg-transparent text-center"
                  />
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell className="border border-gray-300 bg-gray-50 p-2 text-sm font-medium w-2/5">Pass/Fail</TableCell>
              {['critical', 'major1', 'major2', 'minor'].map((defectType) => (
                <TableCell key={defectType} className={cn("border border-gray-300 p-0",
                  defectSummary[defectType].passFail === 'Fail' ? 'bg-red-500' : 'bg-green-500')}>
                  <Input
                    value={defectSummary[defectType].passFail}
                    readOnly
                    className="w-full h-9 border-none bg-transparent text-white text-center"
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>

        <div className="flex mt-5">
          <div className="w-2/3 pr-2">
            <label className="block mb-2 text-sm font-medium">Unclassified Defects</label>
            <Textarea
              placeholder="Enter unclassified defects"
              {...formik.getFieldProps('rqlData.unclassifiedDefects')}
              className="w-full h-[200px] border border-gray-300 rounded-md"
            />
          </div>
          <div className="w-1/3 pl-2">
            <label className="block mb-2 text-sm font-medium">RQL Notes</label>
            <Textarea
              placeholder="Enter RQL notes"
              {...formik.getFieldProps('rqlData.rqlNotes')}
              className="w-full h-[200px] border border-gray-300 rounded-md"
            />
          </div>
        </div>

        <button type="submit" style={{ display: 'none' }}></button>
      </form>
    </div>
  );
}
